import mqtt from 'mqtt';

/**
 * @typedef MqttClientConnOpts
 * @type {object}
 * @property {string} protocol -  mqtt protocol [mqtt]
 * @property {number} keepalive seconds conn keepalive
 * @property {string} protocolId
 * @property {number} protocolVersion
 * @property {number} reconnectInterval
 * @property {number} reconnectPeriod
 */
/**
 * @typedef MqttSubscriptProps
 * @type {object}
 * @property {object} topic
 * @property {number} qos
 */
/**
 * callback for handler of messages received.
 * @callback onMessageCallback
 * @param {string} topic - message topic (string)
 * @param {Buffer} message - message payload
 */

/**
 * Enum for valid log levels.
 * @readonly
 * @enum {string}
 */
 const LOG_LEVELS = { 
  error: 'error',
  warn: 'warn',
  info: 'info',
  http: 'http',
  verbose: 'verbose',
  debug: 'debug',
  silly: 'silly'
};

export default class MqttEnfyoClient {
  /**
   * Create a new mqtt client and make it ready to connect.
   * @param {string} name 
   * @param {string} connUrl 
   * @param {MqttClientConnOpts[]} connOptions 
   * @param {MqttSubscriptProps[]} subscriptionTopics 
   * @param {onMessageCallback} messageHandler 
   */
  constructor(name, connUrl, connOptions, subscriptionTopics = [], messageHandler = undefined) {
    /**
     * mqtt client, managing connection
     * @type {mqtt.MqttClient}
     * @public
     */
    this.client = undefined;
    /**
     * client name (internal mqtt client identifier)
     * @type {string}
     * @public
     */
    this.name = name;
    /**
     * @type {string}
     * @public
     */
    /**
     * @type {boolean}
     * @public
     */
    this.subscribed = false;
    /**
     * @type {Array<string>}
     * @public
     */
    this.topicsSubscribed = [];
    this.connUrl = connUrl;
    this.connOptions = connOptions;
    this.subscriptionTopics = subscriptionTopics
    /** @type {onMessageCallback} */
    this.messageHandler = messageHandler;

    /**
     * handle subscription to mqtt topics
     * @param {Error} err - error
     * @param {MqttSubscriptProps[]} granted - an array of {topic, qos}
     * @returns {void}
     * @private
     */
    this._subscriptCB = (err, granted) => {
      if (err) {
        return this.mqttLogger('error', `MQTT Client (${this.name}) subscribing to topics: ${err.message}`);
      }
      if(!granted?.length){
        return this.mqttLogger('info',`MQTT Client (${this.name}) subscribed to zero topics`);
      }
      this.subscribed = true;
      for(let sub of granted){ 
        this.topicsSubscribed.push(sub.topic);
        return this.mqttLogger('info', `MQTT Client (${this.name}) subscribed to topic ${sub.topic}`);
      }
    }

    this.logListeners = new Map();
    
  }

  /**
   * Starts the mqtt client service, throws errors
   * @returns {Promise<mqtt.MqttClient>}
   * @public
   */
  async start () {
    if (this.client){
      return this.mqttLogger('error', `the mqtt client has already being initialized`);
    }
  
    this.client = mqtt.connect(this.connUrl, this.connOptions);
    this.mqttLogger('info', `MQTT Client (${this.name}) Trying to connect ${this.connUrl}`);
    this.client.on('connect', () => { 
      this.connected = true;
      this.mqttLogger('info', `MQTT Client (${this.name}) online`);
    })
    this.client.on('error', err => { 
      this.mqttLogger('error', `MQTT Client (${this.name}) Connection exception: ${err.message}`);
    });
    this.client.on('reconnect', () => { 
      this.mqttLogger('info', `MQTT Client (${this.name}) trying to recover connection`); 
    });
    this.client.on('offline', () => { 
      this.mqttLogger('warn', `MQTT Client (${this.name}) Connection offline`);
      this.connected = false;
    });
    this.client.on('close', async () => { 
      this.mqttLogger('warn', `MQTT Client (${this.name}) Connection dropped`);
      this.connected = false;
    });

    if(this.subscriptionTopics.length){
      const subList = {};
      for(let t of this.subscriptionTopics){ // change subscription array to library compilant object
        subList[t.topic] = { qos: t.qos??0 }
      }
      this.client.subscribe(subList, {}, this._subscriptCB);
    }

    if(this.messageHandler instanceof Function){
      this.client.on('message', this.messageHandler);
    }
    
    return this.client;
  }

  /**
   * Stops the mqtt client service, throws errors
   * @param {boolean} force - force stop of client service, if not set connections can keep client alive
   * @returns {Promise<boolean>}
   * @public
   */
  async stop (force = false) {
    return new Promise((resolve, reject) => {
      try{
        this.client.end(
          force, 
          { 
            reasonCode: 0,
            properties: { sessionExpiryInterval: 1, reasonString: 'Enfyo gracefully closing mqtt client - Service Stopped' }
          },
          function mqttcloseCallback() { 
            resolve(true); 
          } 
        )
      }catch(err){ reject(err) }
    });
  }
  
  /**
   * Restart the mqtt client service with saved parameters, if stop failed 
   * @param {boolean} [force] - force stop of client service, if not set connections can keep client alive
   * @returns {Promise<boolean>}
   */
  async restart (force = false) {
    await this.stop(force);
    return this.start();
  }

  /**
   * triggers a log event
   * @param {LOG_LEVELS} type 
   * @param {string} description 
   * @private
   */
  mqttLogger(level = LOG_LEVELS.info, description) {
    const eventLog = new CustomEvent('EnfyoMqttClientLog', { detail: {
      level,
      description,
      source: 'mqtt-client'
    } });
    document.dispatchEvent(eventLog);
  }

  /**
   * add a listener to the mqtt connection log 
   * @param {Function} cb 
   */
  onConnectionLog(cb) {
    if(this.logListeners.has(cb)) return ;
    const bubble = (evt) => cb(evt?.detail);

    document.addEventListener('EnfyoMqttClientLog', bubble, false);
    this.logListeners.set(cb, bubble);
  }

  /**
   * removes a listener from the mqtt connection log
   * @param {Function} cb 
   */
  offConnectionLog(cb) {
    if(!this.logListeners.has(cb)) return ;
    const bubble = this.logListeners.get(cb);
    document.removeEventListener('EnfyoMqttClientLog', bubble);
  }
}